import Tooltip from '@/components/Tooltip';
import ToggleButton from '@/components_v2/Buttons/Toggle';
import { Combobox } from '@/components_v2/Combobox/Combobox';
import { Div } from '@/components_v2/inputs/BaseInput/styles';
import { useSurveyForm } from '@/modules/SurveyForm';
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Button,
  Center,
  Checkbox,
  List,
  ListItem,
  Spinner,
} from '@chakra-ui/react';
import React, { useState } from 'react';

import { QuestionProps } from '../../interfaces';
import {
  InfoTooltip,
  InputDiv,
  QuestionContainer,
  QuestionHeader,
  QuestionSubtitle,
  QuestionTitle,
  TooltipContainer,
} from '../common/styles';
import { ToggleContainer } from '../Toggle/styles';
import {
  FormularyGroup,
  FormularySelectProvider,
  useFormularySelect,
  useFormularySelectContext,
} from './use-formulary-select';
import { useTranslation } from 'react-i18next';

type FormularySelectProps = QuestionProps;

function Group(props: { formularyGroup: FormularyGroup }) {
  const { formularyGroup } = props;
  const { selected, toggleItem, toggleMultiple } = useFormularySelectContext();

  const ids = React.useMemo(() => formularyGroup.formularies.map((e) => e.id), [
    formularyGroup.formularies,
  ]);

  const allChecked = React.useMemo(
    () => !!ids.length && ids.every((e) => selected.includes(e)),
    [ids, selected],
  );

  const isIndeterminate = React.useMemo(
    () => ids.some((e) => selected.includes(e)) && !allChecked,
    [allChecked, ids, selected],
  );

  return (
    <AccordionItem border="none">
      <h2>
        <AccordionButton>
          <Box flex="1" textAlign="left" fontWeight="semibold">
            {formularyGroup.name} {`(${formularyGroup.formularies.length})`}
          </Box>
          <AccordionIcon />
        </AccordionButton>
      </h2>
      <AccordionPanel ms="6" pb={4}>
        <List spacing={3}>
          <ListItem>
            <Checkbox
              isChecked={allChecked}
              isIndeterminate={isIndeterminate}
              onChange={() =>
                toggleMultiple(formularyGroup.id, isIndeterminate)
              }
              size="md"
              fontWeight="semibold"
            >
              Todos
            </Checkbox>
          </ListItem>
          {formularyGroup.formularies.map((formulary) => (
            <ListItem key={formulary.id}>
              <Checkbox
                isChecked={selected.includes(formulary.id)}
                onChange={() => toggleItem(formulary.id)}
                size="md"
              >
                {formulary.name}
              </Checkbox>
            </ListItem>
          ))}
        </List>
      </AccordionPanel>
    </AccordionItem>
  );
}

export function FormularySelect({
  onChange,
  initialValues,
  errors,
  question,
  onChangeScheduleTitle,
}: FormularySelectProps) {
  const initialized = React.useRef(false);
  const [onInheritName, setOnInheritName] = useState(false);
  const parsedInitialValues = React.useMemo(() => {
    if (!initialValues?.values || !Array.isArray(initialValues?.values))
      return [];

    return initialValues?.values.map((e: { id: string; name: string }) => e.id);
  }, [initialValues?.values]);

  const { setInheritedTitle } = useSurveyForm();
  const { t } = useTranslation();

  const hook = useFormularySelect(parsedInitialValues);
  const { loading, sectionedData, selected, selectedString, search, setSearch, allForms } = hook;
  const [collapsedIds, setCollapsedIds] = useState<string[]>([]);

  const expandedIndices = React.useMemo(
    () =>
      sectionedData.reduce((acc, group, index) => {
        if (search || !collapsedIds.includes(group.id)) acc.push(index);
        return acc;
      }, [] as number[]),
    [sectionedData, collapsedIds, search],
  );

  const handleAccordionChange = (indices: number | number[]) => {
    const open = Array.isArray(indices) ? indices : [indices];
    setCollapsedIds(
      sectionedData.filter((_, i) => !open.includes(i)).map((g) => g.id),
    );
  };

  React.useEffect(() => {
    if (!initialized.current) {
      initialized.current = true;
      return;
    }

    onChange(
      selected.map((id) => {
        const form = allForms.find((f) => f.id === id);
        return { id, name: form?.name || '' };
      }),
    );
    onChangeScheduleTitle(onInheritName ? selectedString?.split(',')[0] : '');
  }, [
    onChangeScheduleTitle,
    onChange,
    selected,
    selectedString,
    onInheritName,
    allForms,
  ]);

  return (
    <QuestionContainer>
      <QuestionHeader>
        <QuestionTitle>{question.name}</QuestionTitle>
        {question.tipText && (
          <Tooltip description={question.tipText} placement="top right">
            <TooltipContainer>
              <InfoTooltip />
            </TooltipContainer>
          </Tooltip>
        )}
      </QuestionHeader>
      <FormularySelectProvider value={hook}>
        <Combobox.Parent
          placement='auto'
          TriggerComponent={
            <InputDiv>
              <Div>{selectedString || 'Selecione os formulários'}</Div>
            </InputDiv>
          }
          renderFooter={(onClose) => (
            <Box>
              <Button float="right" colorScheme="brand" onClick={onClose}>
                {t('common.confirm')}
              </Button>
            </Box>
          )}
        >
          <Box w="full">
            {loading && !sectionedData.length && (
              <Center>
                <Spinner size="lg" />
              </Center>
            )}
            <Accordion
              index={expandedIndices}
              onChange={handleAccordionChange}
              w="full"
              allowMultiple
            >
              <Combobox.Search onChange={(e) => setSearch(e.target.value)} />
              {sectionedData.map((formularyGroup) => (
                <Group
                  key={formularyGroup.id}
                  formularyGroup={formularyGroup}
                />
              ))}
            </Accordion>
          </Box>
        </Combobox.Parent>
      </FormularySelectProvider>

      {question.inherited_template_name && (
        <ToggleContainer style={{ marginTop: 10 }}>
          <QuestionTitle>Herdar nome do checklist</QuestionTitle>
          <ToggleButton
            state={onInheritName}
            onChange={() => {
              setOnInheritName(!onInheritName);
              setInheritedTitle(!onInheritName);
            }}
          />
        </ToggleContainer>
      )}

      <QuestionSubtitle hasError={!!errors}>
        {errors && errors.errors}
      </QuestionSubtitle>
    </QuestionContainer>
  );
}
